import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { Icon } from '@iconify/react'

import S3FileUpload from 'react-s3/lib/ReactS3'

import { Avatar, Drawer, Input, Spin, Upload, message } from 'antd'

import PDFImg from '../../../assets/images/pdf.png'
import { loginState } from '../../login/login.slice'
import { apiAddJobApplicationNote } from '../store/jobApplicant.service'
import {
    getJobApplicationNotes,
    jobApplicantState,
} from '../store/jobApplicant.slice'
import styles from './JobApplicantNotes.module.css'

const initialNoteState = {
    submitDescription: null,
    attachment: null,
    isSubmit: false,
    fileList: [],
}

const JobApplicantNotes = ({
    state,
    setState,
    canEdit = true,
}) => {
    const params = useParams()
    const dispatch = useDispatch()

    const { userProfile } = useSelector(loginState)
    const jobApplicantData = useSelector(jobApplicantState)

    const [noteState, setNoteState] = useState(initialNoteState)

    const applicantName =
        state?.selectedApplicantData?.fullName || 'Applicant'
    const noteCount = jobApplicantData?.notesList?.length || 0

    const handleClose = () => {
        setState((prevState) => ({
            ...prevState,
            jobApplicantNotesDrawer: false,
        }))
        setNoteState(initialNoteState)
    }

    const handleDownloadResume = async (data) => {
        const resume = data?.attachments

        if (resume?.url && resume?.name) {
            try {
                const response = await fetch(resume.url)
                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }
                const blob = await response.blob()
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = resume.name
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                window.URL.revokeObjectURL(url)
            } catch (error) {
                console.error('Failed to download resume:', error)
            }
        } else {
            console.error('Resume URL or name is missing')
        }
    }

    const uploadS3File = (file, fileList) => {
        const nameChange = file?.name
            .split('.')[0]
            .replace(/\s+/g, '_')
            .concat(`_${dayjs(new Date()).unix()}`)
            .concat(`.${file?.name.split('.')[1]}`)

        const newFileData = new File([file?.originFileObj], nameChange, {
            type: file?.type,
        })

        window.Buffer = window.Buffer || require('buffer').Buffer

        setNoteState((prevState) => ({
            ...prevState,
            isSubmit: true,
        }))

        S3FileUpload.uploadFile(newFileData, userProfile?.s3Config)
            .then(async (data) => {
                setNoteState((prevState) => ({
                    ...prevState,
                    attachment: {
                        name: file?.name,
                        url: data?.location,
                        fileType: file?.type,
                    },
                    isSubmit: false,
                    fileList: fileList,
                }))
            })
            .catch(() => {
                setNoteState((prevState) => ({
                    ...prevState,
                    attachment: null,
                    isSubmit: false,
                }))
                message.error('Upload Failed!. Please Upload again')
            })
    }

    const uploadProps = {
        name: 'file',
        accept: '.pdf',
        beforeUpload: (file, fileList) => {
            uploadS3File(file, fileList)
            return false
        },
        maxCount: 1,
        showUploadList: false,
        onRemove() {
            setNoteState((prevState) => ({
                ...prevState,
                attachment: null,
                fileList: [],
            }))
        },
    }

    const clearAttachment = () => {
        setNoteState((prevState) => ({
            ...prevState,
            attachment: null,
            fileList: [],
        }))
    }

    const handleSubmitNotes = async () => {
        if (!canEdit) return
        if (!noteState?.submitDescription?.trim() || noteState?.isSubmit) {
            return
        }

        try {
            const data = {
                description: noteState?.submitDescription,
                attachments: noteState?.attachment,
                userId: state?.selectedApplicantData?.candidateId,
                jobId: params?.id,
                createBy: userProfile?._id,
            }

            setNoteState((prevState) => ({
                ...prevState,
                isSubmit: true,
            }))

            const res = await apiAddJobApplicationNote(data)

            if (res?.data?.success) {
                message.success('Note submitted successfully')
                setNoteState(initialNoteState)
                dispatch(
                    getJobApplicationNotes({
                        userId: state?.selectedApplicantData?.candidateId,
                    })
                )
            } else {
                message.error('Failed to submit note')
                setNoteState((prev) => ({ ...prev, isSubmit: false }))
            }
        } catch (error) {
            message.error('Failed to submit note')
            setNoteState((prev) => ({ ...prev, isSubmit: false }))
        }
    }

    useEffect(() => {
        if (state?.selectedApplicantData?.candidateId) {
            dispatch(
                getJobApplicationNotes({
                    userId: state?.selectedApplicantData?.candidateId,
                })
            )
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.selectedApplicantData?._id])

    const canSubmit =
        Boolean(noteState?.submitDescription?.trim()) && !noteState?.isSubmit

    return (
        <Drawer
            placement="right"
            open={state?.jobApplicantNotesDrawer}
            onClose={handleClose}
            width={480}
            className={styles.notesDrawer}
            rootClassName={styles.notesDrawerRoot}
            closable={false}
            destroyOnClose
            title={
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerHeaderLeft}>
                        <div className={styles.drawerHeaderIcon}>
                            <Icon
                                icon="mdi:note-text-outline"
                                width={20}
                                height={20}
                            />
                        </div>
                        <div>
                            <div className={styles.drawerHeaderTitle}>
                                View Notes
                            </div>
                            <div className={styles.drawerHeaderSubtitle}>
                                Notes for {applicantName}
                            </div>
                        </div>
                    </div>
                    <div className={styles.drawerHeaderActions}>
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={handleClose}
                            aria-label="Close notes"
                        >
                            <Icon icon="mdi:close" width={18} height={18} />
                        </button>
                    </div>
                </div>
            }
        >
            <div className={styles.layout}>
                <section className={styles.notesPane}>
                    <div className={styles.notesPaneHeader}>
                        <p className={styles.notesPaneTitle}>Activity</p>
                        {!jobApplicantData?.notesLoading && noteCount > 0 ? (
                            <span className={styles.countPill}>
                                {noteCount}{' '}
                                {noteCount === 1 ? 'note' : 'notes'}
                            </span>
                        ) : null}
                    </div>

                    <div className={styles.notesScroll}>
                        {jobApplicantData?.notesLoading ? (
                            <div className={styles.centeredState}>
                                <Spin />
                            </div>
                        ) : noteCount > 0 ? (
                            <div className={styles.notesList}>
                                {jobApplicantData.notesList.map((data) => (
                                    <article
                                        key={data?._id || data?.createdAt}
                                        className={styles.noteCard}
                                    >
                                        <div className={styles.noteTop}>
                                            {data?.createBy
                                                ?.profilePicture ? (
                                                <Avatar
                                                    size={34}
                                                    className={
                                                        styles.noteAvatar
                                                    }
                                                    src={
                                                        data.createBy
                                                            .profilePicture
                                                    }
                                                />
                                            ) : (
                                                <Avatar
                                                    size={34}
                                                    className={
                                                        styles.noteAvatar
                                                    }
                                                >
                                                    {data?.createBy
                                                        ?.name?.[0] || '?'}
                                                </Avatar>
                                            )}
                                            <div className={styles.noteMeta}>
                                                <p
                                                    className={
                                                        styles.noteAuthor
                                                    }
                                                >
                                                    {data?.createBy?.name ||
                                                        'Unknown'}
                                                </p>
                                                <p
                                                    className={styles.noteTime}
                                                >
                                                    {data?.createdAt
                                                        ? dayjs(
                                                              data.createdAt
                                                          ).format(
                                                              'DD MMM YYYY · hh:mm A'
                                                          )
                                                        : '—'}
                                                </p>
                                            </div>
                                        </div>

                                        {data?.description ? (
                                            <p className={styles.noteContent}>
                                                {data.description}
                                            </p>
                                        ) : null}

                                        {data?.attachments ? (
                                            <button
                                                type="button"
                                                className={styles.attachmentBtn}
                                                onClick={() =>
                                                    handleDownloadResume(data)
                                                }
                                            >
                                                {data?.attachments?.fileType
                                                    ?.toLowerCase()
                                                    .includes('pdf') ? (
                                                    <img
                                                        src={PDFImg}
                                                        alt=""
                                                        width={18}
                                                        height={18}
                                                    />
                                                ) : (
                                                    <Icon
                                                        icon="mdi:paperclip"
                                                        width={14}
                                                        height={14}
                                                    />
                                                )}
                                                <span
                                                    className={
                                                        styles.attachmentName
                                                    }
                                                >
                                                    {data?.attachments?.name}
                                                </span>
                                                <Icon
                                                    icon="mdi:download-outline"
                                                    width={14}
                                                    height={14}
                                                />
                                            </button>
                                        ) : null}
                                    </article>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.centeredState}>
                                <div className={styles.emptyIcon}>
                                    <Icon
                                        icon="mdi:note-off-outline"
                                        width={24}
                                        height={24}
                                    />
                                </div>
                                <p className={styles.emptyTitle}>
                                    No notes yet
                                </p>
                                <p className={styles.emptyText}>
                                    {canEdit
                                        ? 'Add the first note using the form below.'
                                        : 'No notes have been added yet.'}
                                </p>
                            </div>
                        )}
                    </div>
                </section>

                {canEdit ? (
                <section className={styles.composerPane}>
                    <div className={styles.composerCard}>
                        <div>
                            <p className={styles.composerLead}>Add a note</p>
                            <p className={styles.composerHint}>
                                Share feedback, interview updates, or next steps
                                for this applicant.
                            </p>
                        </div>

                        <Input.TextArea
                            value={noteState?.submitDescription || ''}
                            onChange={(event) =>
                                setNoteState((prevState) => ({
                                    ...prevState,
                                    submitDescription: event.target.value,
                                }))
                            }
                            placeholder="Write a note…"
                            className={styles.composerTextarea}
                            autoSize={{ minRows: 3, maxRows: 6 }}
                        />

                        <div className={styles.composerToolbar}>
                            <div className={styles.leftTools}>
                                <Upload
                                    fileList={noteState.fileList}
                                    {...uploadProps}
                                >
                                    <button
                                        type="button"
                                        className={styles.attachBtn}
                                    >
                                        <Icon
                                            icon="mdi:paperclip"
                                            width={16}
                                            height={16}
                                        />
                                        Attachment
                                    </button>
                                </Upload>
                                {noteState?.attachment?.name ? (
                                    <span className={styles.fileChip}>
                                        <span className={styles.fileChipName}>
                                            {noteState.attachment.name}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.fileChipRemove}
                                            onClick={clearAttachment}
                                            aria-label="Remove attachment"
                                        >
                                            <Icon
                                                icon="mdi:close"
                                                width={14}
                                                height={14}
                                            />
                                        </button>
                                    </span>
                                ) : null}
                            </div>

                            <button
                                type="button"
                                className={styles.submitBtn}
                                onClick={handleSubmitNotes}
                                disabled={!canSubmit}
                            >
                                {noteState?.isSubmit ? (
                                    <>
                                        <Icon
                                            icon="line-md:loading-loop"
                                            width={16}
                                            height={16}
                                        />
                                        Adding...
                                    </>
                                ) : (
                                    'Add Note'
                                )}
                            </button>
                        </div>
                    </div>
                </section>
                ) : null}
            </div>
        </Drawer>
    )
}

export default JobApplicantNotes
